/**
 * API Client Configuration
 * 
 * Axios instance configured for API requests.
 * Prepared for future backend integration with JWT token attachment.
 */

import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import config from '../config/config';

// Create Axios instance
const apiClient = axios.create({
  baseURL: config.api.baseUrl,
  timeout: config.api.timeout,
  headers: {
    'Content-Type': 'application/json'
  }
});

// ---------------------------------------------------------------------------
// Active team injection (multi-team support, requirements 17.6 / 17.7)
// ---------------------------------------------------------------------------

/** localStorage key written by `TeamContext` (see task 11.1). */
export const ACTIVE_TEAM_STORAGE_KEY = 'reporthub.active_team_id';

/** Header name carried on every tenant-scoped request. */
export const ACTIVE_TEAM_HEADER = 'X-Active-Team-Id';

/**
 * Path patterns that must NOT receive `X-Active-Team-Id`.
 *
 * These endpoints either resolve the caller's team membership (so the header
 * would be circular) or operate on Team records themselves (where the
 * `team_id` is already identified in the URL path). Sending the header to
 * them is harmless on the server, but the design (R17.6) restricts the
 * header to "tenant-scoped requests only", so we are explicit here.
 *
 * Patterns are matched against the request path (with any baseURL prefix
 * stripped), so they work whether callers pass `/teams` or `/api/v1/teams`.
 */
export const TENANT_HEADER_OPT_OUT_PATTERNS = Object.freeze([
  /^\/users\/me\/teams(\/|\?|#|$)/,           // GET /users/me/teams
  /^\/users\/[^/]+\/teams(\/|\?|#|$)/,        // GET /users/{user_id}/teams
  /^\/users\/[^/]+\/team-assignment(\/|\?|#|$)/, // POST /users/{user_id}/team-assignment
  /^\/users\/check-auth(\/|\?|#|$)/,          // GET /users/check-auth (app-level auth check)
  /^\/teams(\/|\?|#|$)/,                       // /teams listing + /teams/{team_id}/...
]);

/**
 * Read the active team id written by `TeamContext`.
 *
 * Returns `null` when no team is selected, when the synthetic "All teams"
 * option is active (TeamContext clears or sentinels the value), or when
 * `localStorage` is unavailable. Reading at request time avoids tying the
 * HTTP client to React lifecycle.
 */
export function readActiveTeamId() {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(ACTIVE_TEAM_STORAGE_KEY);
    if (raw == null) return null;
    const trimmed = raw.trim();
    if (trimmed === '' || trimmed === 'null' || trimmed === 'undefined' || trimmed === 'ALL') {
      return null;
    }
    return trimmed;
  } catch (_e) {
    // localStorage may be blocked (privacy mode, SSR, sandboxed iframe).
    return null;
  }
}

/**
 * Decide whether the active-team header should be skipped for a given
 * axios request config. Skipped when:
 *   1. The caller opts out explicitly via `skipActiveTeamHeader: true`.
 *   2. The path matches one of the non-tenant-scoped patterns above.
 */
export function shouldSkipActiveTeamHeader(requestConfig) {
  if (!requestConfig) return true;
  if (requestConfig.skipActiveTeamHeader === true) return true;

  const rawUrl = requestConfig.url || '';
  let path = rawUrl;

  // Absolute URL → take pathname.
  if (/^https?:\/\//i.test(rawUrl)) {
    try {
      path = new URL(rawUrl).pathname;
    } catch (_e) {
      path = rawUrl;
    }
  }

  // Strip the api baseURL (e.g. "/api/v1") so opt-out patterns can be written
  // relative to the service surface regardless of where callers pass them in.
  const baseUrl = (requestConfig.baseURL || config.api.baseUrl || '').replace(/\/$/, '');
  if (baseUrl && path.startsWith(baseUrl)) {
    path = path.slice(baseUrl.length);
  }

  if (!path.startsWith('/')) path = `/${path}`;

  return TENANT_HEADER_OPT_OUT_PATTERNS.some((pattern) => pattern.test(path));
}

/**
 * Compute SHA-256 hex digest of a string using Web Crypto API.
 * Required for CloudFront OAC → Lambda Function URL: POST/PUT/PATCH
 * requests must include x-amz-content-sha256 so OAC can correctly
 * sign the payload. Without it, Lambda Function URL rejects with 403.
 */
async function sha256Hex(message) {
  const msgBuffer = new TextEncoder().encode(message);
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Request Interceptor
 * - Attaches JWT token to all requests
 * - Attaches X-Active-Team-Id on tenant-scoped requests when an active team is selected
 * - Computes x-amz-content-sha256 for POST/PUT/PATCH (required by Lambda Function URL OAC)
 */
apiClient.interceptors.request.use(
  async (requestConfig) => {
    try {
      // Get current auth session
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      if (token) {
        requestConfig.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      console.warn('Failed to attach auth token:', error);
    }

    // Inject the active-team header on tenant-scoped requests only.
    // Omitted for admin "All teams" (activeTeamId === null) and for the
    // explicit non-tenant-scoped opt-out endpoints (R17.6, R17.7).
    // Skip if the caller already set the header explicitly (form-level override).
    if (!shouldSkipActiveTeamHeader(requestConfig) && !requestConfig.headers[ACTIVE_TEAM_HEADER]) {
      const activeTeamId = readActiveTeamId();
      if (activeTeamId) {
        requestConfig.headers[ACTIVE_TEAM_HEADER] = activeTeamId;
      }
    }

    // CloudFront OAC + Lambda Function URL requires x-amz-content-sha256
    // for requests with a body, otherwise the payload signature check fails (403).
    // For bodyless POST/PUT/PATCH, send the hash of an empty string.
    const method = (requestConfig.method || '').toUpperCase();
    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
      const body = requestConfig.data != null
        ? (typeof requestConfig.data === 'string' ? requestConfig.data : JSON.stringify(requestConfig.data))
        : '';
      requestConfig.headers['x-amz-content-sha256'] = await sha256Hex(body);
    }

    return requestConfig;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Response Interceptor
 * Handles common error scenarios
 */
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle common error scenarios
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;

      switch (status) {
        case 401:
          console.error('Unauthorized - redirecting to login');
          // TODO: Trigger logout and redirect to login
          break;
        case 403:
          console.error('Forbidden - insufficient permissions');
          break;
        case 404:
          console.error('Resource not found');
          break;
        case 500:
          console.error('Server error');
          break;
        default:
          console.error('API error:', status, data);
      }
    } else if (error.request) {
      // Request made but no response received
      console.error('Network error - no response from server');
    } else {
      // Error in request setup
      console.error('Request error:', error.message);
    }

    return Promise.reject(error);
  }
);

export default apiClient;
